import { Button, Card, CardContent, CardHeader, CardTitle } from '@tark/shared/components';
import { ArrowRight, CalendarClock } from 'lucide-react';
import type { CoachTodayTimelineProps } from './coachStudioTypes';
import { formatCompactDate, formatSessionWindow } from './coachStudioUtils';

export default function CoachTodayTimeline({
  occurrences,
  totalTodayCount,
  workTabLabel,
  onOpenWork,
}: CoachTodayTimelineProps) {
  return (
    <Card variant="default" padding="none" className="overflow-hidden">
      <CardHeader className="flex flex-col gap-3 border-b border-border sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle>{totalTodayCount > 0 ? 'Today' : 'Upcoming'}</CardTitle>
          <p className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-text-subtle">
            {totalTodayCount > 0 ? `${totalTodayCount} today` : `${occurrences.length} next`}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={onOpenWork}>
          {workTabLabel}
        </Button>
      </CardHeader>

      <CardContent className="p-0">
        {occurrences.length > 0 ? (
          <div className="divide-y divide-border">
            {occurrences.slice(0, 3).map((occurrence, index) => (
              <button
                key={occurrence.occurrenceId}
                type="button"
                onClick={onOpenWork}
                className="group grid w-full gap-3 px-4 py-4 text-left transition-colors hover:bg-surface-elevated/40 sm:grid-cols-[96px_minmax(0,1fr)_auto] sm:items-center md:px-5"
              >
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">
                    {formatCompactDate(occurrence.startsAt)}
                  </p>
                  <p className="mt-1 text-sm font-semibold tabular-nums text-text">
                    {formatSessionWindow(occurrence.startsAt, occurrence.endsAt)}
                  </p>
                </div>

                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate font-semibold text-text">{occurrence.title}</p>
                    {index === 0 ? (
                      <span className="rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] [color:var(--color-on-primary)]">
                        Next
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 truncate text-sm text-text-muted">
                    {occurrence.locationLabel || occurrence.programName || 'Session'}
                  </p>
                </div>

                <ArrowRight className="hidden h-4 w-4 text-text-subtle transition-transform group-hover:translate-x-0.5 group-hover:text-primary sm:block" />
              </button>
            ))}
          </div>
        ) : (
          <div className="m-5 rounded-2xl border border-dashed border-border bg-surface-elevated/20 p-6 text-center">
            <CalendarClock className="mx-auto h-6 w-6 text-text-subtle" />
            <p className="font-semibold text-text">No sessions yet</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
